import React, { memo } from "react"
import { ClipLoader } from "react-spinners"
import { COLORS } from "../../constants/colors"
import { ActionButtonVariant, getButtonVariantMetadata } from "../../models/enum/ActionButtonVariant"

/**
 * Props for the CustomButton component.
 */
interface ButtonProps {
  /**
   * The variant of the button. This controls both the style and the action.
   * Default value is 'PRIMARY'.
   */
  variant: ActionButtonVariant

  /**
   * Function to be called when the button is clicked.
   */
  onClick?: () => void

  /**
   * Function to be called when the button is clicked.
   * Accepts an optional form event.
   */
  onSubmit?: (e?: React.FormEvent) => void

  /**
   * Text displayed inside the button.
   */
  label?: string

  /**
   * Disables the button when true.
   */
  disabled?: boolean

  /**
   * Unique identifier for the button.
   */
  id: string
}

/**
 * CustomButton component renders a button with configurable styles and behavior.
 *
 * @param {ButtonProps} props - The properties for the CustomButton component.
 * @returns {JSX.Element} The rendered button component.
 */
const ActionButton: React.FC<ButtonProps> = memo(({ variant, onClick, onSubmit, label, disabled, id }) => {
  const metaData = getButtonVariantMetadata(variant)
  const className = metaData.className
  const title = label ?? metaData.label

  const clickAction = onSubmit ?? onClick

  return (
    <button
      data-automation-id={id}
      id={id}
      onClick={clickAction}
      disabled={disabled}
      type="button"
      className={`transition duration-200 ${className} focus:shadow-sm focus:ring-4 focus:ring-opacity-50 w-full text-sm shadow-sm hover:shadow-md font-semibold text-center inline-block`}
    >
      { variant == ActionButtonVariant.SAVE && disabled ? <ClipLoader size={20} color={COLORS.WHITE} /> : title }
    </button>
  )
})

export default ActionButton